import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { RefreshButton } from "components/DesignSystem/RefreshButton/RefreshButton";
import { REFRESH_PAGE } from "constants/refreshPage";
import { Search } from "components/DesignSystem/Search/Search";
import Slider from "components/DesignSystem/Slider/Slider";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { EmptyInvoiceList } from "components/Illustrations/EmptyInvoiceList";
import {
  InvoiceFilter,
  InvoiceFilterType,
} from "components/InvoiceFilter/InvoiceFilter";
import { SetupInoviceSetting } from "components/SetupInoviceSetting/SetupInoviceSetting";
import {
  INVOICE_ADD_CLICKED,
  INVOICE_HOME_VISITED,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { UpdateFilterFunction } from "hooks/useFilter";
import { ModalClose, useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDebounce } from "utils/debounce";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import {
  useCreateEntityInvoiceMutation,
  useGetAllInvoicesQuery,
  useGetInvoiceSettingsQuery,
  usePopulateBulkInvoiceMutation,
  useUploadBulkInvoiceMutation,
} from "store/apis/invoices";
import { BULK_INVOICE_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import { BulkInvoiceUpload } from "./BulkInvoiceUpload";
import { InvoiceSlider } from "./InvoiceSlider";
import { SourceSyncBanner } from "components/InvoiceSourceSync/SourceSyncBanner";

import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { CurrentGroupContextT } from "contexts/CurrentGroupContext";
import { RowSelectionState, SortingState } from "react-table-8.10.7";
import { InvoiceSettingsType } from "types/Models/invoice";
import { InvoiceTable } from "./InvoiceTable";
import TableSelectionControls from "./TableSelectionControls";
import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import { LockedUi } from "components/LockedUi/LockedUi";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { currency } from "utils/Currency";

const INVOICE_REFRESH_ENDPOINTS = ["getAllInvoices"];

const ConsoleRow = ({
  label,
  amount,
  note,
  isTotal = false,
}: {
  label: ReactNode;
  amount: number;
  note?: string;
  isTotal?: boolean;
}) => (
  <div className="t-flex t-flex-col t-gap-0.5">
    <div className="t-flex t-items-center t-justify-between t-gap-2">
      {label}
      <span
        className={classNames("t-text-text-100", {
          "t-font-semibold": isTotal,
        })}
      >
        <AmountSuperScript amount={amount} />
      </span>
    </div>
    {note && (
      <span className="t-text-caption t-italic t-text-text-30 t-text-right">
        {note}
      </span>
    )}
  </div>
);

export const InvoiceConsole = ({
  due,
  paid,
  writtenOff,
  total,
  bordered = true,
}: {
  total: {
    label: string;
    amount: number;
  };
  paid: {
    label: string;
    amount: number;
  };
  writtenOff?: {
    label: string;
    amount: number;
  };
  due: {
    label: string;
    amount: number;
    overdueAmount: number;
  };
  bordered?: boolean;
}) => {
  const { isMobile } = useWindowSize();

  const dueBreakdown = `[Due: ${currency({
    amount: due.amount - due.overdueAmount,
    maximumFractionDigits: 0,
  })} + Overdue: ${currency({
    amount: due.overdueAmount,
    maximumFractionDigits: 0,
  })}]`;

  if (isMobile) {
    return (
      <div
        className={classNames(
          "t-flex t-flex-col t-gap-2 t-p-3 t-rounded t-text-body-sm",
          { "t-border-solid t-border-neutral-0 t-border": bordered }
        )}
      >
        <ConsoleRow
          label={
            <Tag transparent tagType="green">
              <span className="t-text-text-30">{paid.label}</span>
            </Tag>
          }
          amount={paid.amount}
        />
        {writtenOff && writtenOff.amount > 0 && (
          <ConsoleRow
            label={
              <Tag transparent tagType="gray">
                <span className="t-text-text-30">{writtenOff.label}</span>
              </Tag>
            }
            amount={writtenOff.amount}
          />
        )}
        <ConsoleRow
          label={
            <Tag transparent tagType="orange">
              <span className="t-text-text-30">{due.label}</span>
            </Tag>
          }
          amount={due.amount}
          note={dueBreakdown}
        />
        <div className="t-border-0 t-border-t t-border-solid t-border-neutral-10" />
        <ConsoleRow
          label={<span className="t-text-text-30">{total.label}</span>}
          amount={total.amount}
          isTotal
        />
      </div>
    );
  }

  return (
    <div
      className={classNames("t-flex t-gap-9 t-p-5 t-rounded", {
        "t-border-solid t-border-neutral-0 t-border": bordered,
      })}
    >
      <div className="t-flex t-gap-1 t-flex-col">
        <span className="t-text-text-30 t-text-body-sm">{total.label}</span>
        <div className="t-text-subtext t-text-text-100">
          <AmountSuperScript amount={total.amount} />
        </div>
      </div>

      <p className="t-m-0 t-text-text-100 t-text-subtext t-self-center">=</p>

      <div className="t-flex t-gap-1 t-flex-col">
        <Tag transparent tagType="green">
          <span className="t-text-text-30">{paid.label}</span>
        </Tag>
        <div className="t-text-subtext t-text-text-100 t-ml-3.5">
          <AmountSuperScript amount={paid.amount} />
        </div>
      </div>

      {writtenOff && writtenOff.amount > 0 && (
        <>
          <p className="t-m-0 t-text-text-100 t-text-subtext t-self-center">
            +
          </p>
          <div className="t-flex t-gap-1 t-flex-col">
            <Tag transparent tagType="gray">
              <span className="t-text-text-30">{writtenOff.label}</span>
            </Tag>
            <div className="t-text-subtext t-text-text-100 t-ml-3.5">
              <AmountSuperScript amount={writtenOff.amount} />
            </div>
          </div>
        </>
      )}

      <p className="t-m-0 t-text-text-100 t-text-subtext t-self-center">+</p>

      <div className="t-flex t-gap-1 t-flex-col">
        <Tag transparent tagType="orange">
          <span className="t-text-text-30">{due.label}</span>
        </Tag>
        <div className="t-flex t-items-baseline t-gap-2 t-ml-3.5">
          <span className="t-text-subtext t-text-text-100">
            <AmountSuperScript amount={due.amount} />
          </span>
          <span className="t-text-body-sm t-italic t-text-text-30">
            {dueBreakdown}
          </span>
        </div>
      </div>
    </div>
  );
};

export const SettingsRedirectConfirmation = ({
  isOpen,
  close,
  url,
}: {
  isOpen: boolean;
  close: () => void;
  url: string;
}) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Settings required</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body>
          <p>
            Invoice settings are needs to be filled before creating invoice.
          </p>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button>Cancel</Button>
          </Modal.RawClose>
          <ConditionalLink to={`${url}`}>
            <Button customType="primary">Go to invoice settings</Button>
          </ConditionalLink>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

export const InvoiceList = ({
  group,
  goToSettings,
  invoiceSetting,
  filter,
  updateFilter,
  rowSelection,
  setRowSelection,
}: {
  group: CurrentGroupContextT;
  invoiceSetting: InvoiceSettingsType | null | undefined;
  goToSettings: {
    isOpen: boolean;
    showConfirmation: boolean;
    open: () => void;
    dismissConfirmation: () => void;
    close: ModalClose;
    toggle: ModalClose;
  };
  filter: InvoiceFilterType;
  updateFilter: UpdateFilterFunction<InvoiceFilterType>;
  rowSelection?: RowSelectionState;
  setRowSelection?: (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
}) => {
  usePageTitle("Invoicing");
  const history = useHistory();
  const { search } = useLocation();
  const { update } = useUpdateQuery();
  const { url } = useRouteMatch();

  const query = useQuery();
  const currentPage = Number(query.get("page")) || 1;
  const pageSize = query.get("page_size");

  const entityId = useCurrentEntityId();
  const [invoiceId, setInvoiceId] = useState<string>("");
  const openBulkUpload = useModal();

  const allSelectedInvoices = useMemo(
    () =>
      Object.entries(rowSelection ?? {})
        .filter(([_, isSelected]) => isSelected)
        .map(
          ([key]) =>
            JSON.parse(key) as {
              uuid: string;
              invoice_amount: string | null;
              due_balance: string | null;
              amount_in_base_currency: number | null;
            }
        ),
    [rowSelection]
  );

  const selectedInvoiceIds = useMemo(
    () => allSelectedInvoices.map((row) => row.uuid),
    [allSelectedInvoices]
  );

  const uuidRowSelection = useMemo(
    () => Object.fromEntries(selectedInvoiceIds.map((id) => [id, true])),
    [selectedInvoiceIds]
  );

  const onSelectionChange = (invoiceIds?: string[]) => {
    if (setRowSelection) {
      setRowSelection((prev) => {
        const newSelection: RowSelectionState = {};
        invoiceIds?.forEach((id) => {
          newSelection[id] = true;
        });
        return newSelection;
      });
    }
  };

  const {
    open: openSlider,
    isOpen: isOpenSlider,
    close: closeSlider,
  } = useModal();

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "INVOICE_DATE",
      desc: true,
    },
  ]);

  const { data: invoiceSettingData, isSuccess: settingsLoaded } =
    useGetInvoiceSettingsQuery(
      {
        groupId: group?.uuid!,
        entityId,
      },
      { skip: !group?.uuid || !entityId }
    );

  const searchTerm = query.get("search");
  const [searchInput, setSearchInput] = useState(searchTerm ?? "");
  const { isMobile } = useWindowSize();

  const refreshButton = (
    <RefreshButton
      endpoints={INVOICE_REFRESH_ENDPOINTS}
      page={REFRESH_PAGE.INVOICES}
    />
  );
  const debouncedSearch = useDebounce(searchInput);

  useEffect(() => {
    update({ query: "search", value: debouncedSearch || null });
  }, [debouncedSearch]);

  const handleClearSearch = () => setSearchInput("");

  const sortCol = sorting[0]?.id as
    | "INVOICE_DATE"
    | "DUE_DATE"
    | "SENT_TO"
    | "AMOUNT"
    | undefined;
  const sortOrder = sorting[0]?.desc;

  const {
    data: allInvoices,
    isLoading,
    isFetching,
  } = useGetAllInvoicesQuery(
    {
      currentPage,
      groupId: group?.uuid!,
      entityId,
      searchTerm: debouncedSearch || null,
      sortCol,
      sortOrder: sortOrder ? ("DSC" as const) : ("ASC" as const),
      minAmount: filter.MIN_AMOUNT,
      maxAmount: filter.MAX_AMOUNT,
      startDate: filter.INVOICE_START_DATE,
      endDate: filter.INVOICE_END_DATE,
      dueDateStartDate: filter.DUEDATE_START_DATE,
      dueDateEndDate: filter.DUEDATE_END_DATE,
      markedVoidAtStartDate: filter.VOID_START_DATE,
      markedVoidAtEndDate: filter.VOID_END_DATE,
      markedUncollectibleAtStartDate: filter.UNCOLLECTIBLE_START_DATE,
      markedUncollectibleAtEndDate: filter.UNCOLLECTIBLE_END_DATE,
      customerIds: filter.CUSTOMER.join(","),
      customerStates: filter.CUSTOMER_STATE,
      statuses: filter.STATUS,
      statusGroup: filter.STATUS_GROUP,
      sources: filter.SOURCE,
      invoiceType: filter.INVOICE_TYPE,
      limit: pageSize ? Number(pageSize) : undefined,
    },
    {
      skip: !group?.uuid || !entityId,
    }
  );

  const [createInvoice, { isLoading: creatingInvoice }] =
    useCreateEntityInvoiceMutation();

  const { invoices } = allInvoices || {};

  const data = useMemo(() => invoices || [], [invoices]);

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent(INVOICE_HOME_VISITED);
  }, [trackEvent]);

  const onRowClick = (invoiceId: string, status: string) => {
    setInvoiceId(invoiceId);
    openSlider();
  };

  const onCreateInvoice = async (skipSettingsCheck: boolean = false) => {
    if (!invoiceSetting && !skipSettingsCheck) {
      goToSettings.open();
      return;
    }

    trackEvent(INVOICE_ADD_CLICKED);

    if (group.uuid && invoiceSetting?.entity_id) {
      try {
        const invoice = await createInvoice({
          groupId: group?.uuid,
          entityId: invoiceSetting?.entity_id,
        }).unwrap();

        history.push(`/books/invoices-and-customers/${invoice.uuid}${search}`);
      } catch (error) {}
    }
  };

  const subtotal = useMemo(
    () =>
      allSelectedInvoices.reduce(
        (sum, row) =>
          sum +
          Number(
            row.amount_in_base_currency ||
              row.due_balance ||
              row.invoice_amount ||
              0
          ),
        0
      ),
    [allSelectedInvoices]
  );

  if (settingsLoaded && !invoiceSetting?.uuid) {
    return <SetupInoviceSetting context="invoices" />;
  }
  return (
    <>
      <div className="t-text-text-60 t-relative t-h-full">
        {isLoading && (
          <div className="t-flex t-h-full t-w-full t-items-center t-justify-center t-py-9">
            <Loader />
          </div>
        )}

        {invoices && !isLoading && (
          <div className="t-h-full t-flex t-flex-col t-overflow-hidden sm:t-overflow-y-auto">
            <DashboardContainer className="t-gap-4">
              <DashboardContainer.Header sticky className="sm:!t-static">
                <div className="t-bg-surface t-flex t-flex-col t-gap-4 t-mt-4 sm:t-gap-5 sm:t-mt-0">
                  <SourceSyncBanner />
                  {allInvoices && (
                    <InvoiceConsole
                      total={{
                        label: "Total invoiced amount",
                        amount: allInvoices.total_amount,
                      }}
                      due={{
                        label: "Outstanding",
                        amount: allInvoices.due_amount,
                        overdueAmount: allInvoices.overdue_amount || 0,
                      }}
                      paid={{ label: "Paid", amount: allInvoices.paid_amount }}
                      writtenOff={{
                        label: "Written off",
                        amount: allInvoices.written_off_amount || 0,
                      }}
                    />
                  )}
                  <div className="t-flex t-gap-4 sm:t-flex-col sm:t-gap-2">
                    <div className="t-flex t-gap-2 t-w-full t-items-center t-flex-wrap sm:t-flex-nowrap">
                      <Search
                        isLoading={isFetching}
                        className="t-w-[18%] focus:t-w-3/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search"
                      />
                      <InvoiceFilter
                        filter={filter}
                        updateFilter={updateFilter}
                      />
                      {isMobile && refreshButton}
                    </div>
                    <div className="t-grow t-ml-auto t-shrink-0 t-flex t-justify-end t-items-center t-gap-2 sm:t-ml-0 sm:t-grow-0 sm:t-self-end">
                      <PaginationHandler
                        page_num={currentPage}
                        per_page={allInvoices?.per_page || 1}
                        total_count={allInvoices?.total_count || 0}
                        total_pages={allInvoices?.total_pages || 1}
                        isLoading={isFetching}
                        showOptions
                      />
                      {!isMobile && refreshButton}
                    </div>
                  </div>
                </div>
              </DashboardContainer.Header>
            </DashboardContainer>

            <div className="t-flex t-gap-3 t-flex-col t-flex-1 t-min-h-0 t-overflow-hidden sm:t-flex-none sm:t-overflow-visible sm:t-gap-0 sm:t-pt-5">
              <div className="t-flex t-gap-5 t-flex-col t-sticky t-top-0 t-z-header t-bg-white sm:t-hidden">
                <div className="t-flex t-justify-between t-gap-5">
                  <div className="t-flex t-justify-between t-gap-2"></div>
                </div>
              </div>
              {invoices.length > 0 && (
                <InvoiceTable
                  invoicesData={allInvoices?.invoices}
                  onRowClick={onRowClick}
                  rowSelection={rowSelection}
                  setRowSelection={setRowSelection}
                  sorting={sorting}
                  setSorting={setSorting}
                  className="t-overflow-auto t-h-full t-pb-20 scrollbar-visible"
                />
              )}

              {invoices.length === 0 && !isLoading && (
                <div className="t-flex t-flex-col t-items-center t-justify-center t-flex-1 t-text-text-60 t-gap-3">
                  <EmptyInvoiceList />
                  {searchTerm ? (
                    <>
                      <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                        <p className="t-m-0 t-text-subtitle-sm">
                          No results found for your search
                        </p>
                        <p className="t-m-0 t-text-body-sm">
                          Try clearing the search.
                        </p>
                      </div>
                      <Button
                        onClick={handleClearSearch}
                        size="small"
                        customType="primary"
                      >
                        Clear search
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                        <p className="t-m-0 t-text-subtitle-sm">
                          Create your first invoice
                        </p>
                        <p className="t-m-0 t-text-body-sm">
                          Send customised invoices to your customers.
                        </p>
                      </div>
                      <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
                        <Button
                          onClick={() => onCreateInvoice(true)}
                          isLoading={creatingInvoice}
                          disabled={creatingInvoice}
                          size="small"
                          customType="primary"
                        >
                          Create invoice
                        </Button>
                      </LockedUi>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <SettingsRedirectConfirmation
        isOpen={goToSettings.isOpen}
        close={goToSettings.close}
        url={`${url}/settings${search}`}
      />

      {selectedInvoiceIds.length > 0 && (
        <SelectedFloatingControl>
          <>
            <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
              {selectedInvoiceIds.length} selected
            </span>
            <span className="t-text-neutral-10 t-h-full">
              <DividerLine />
            </span>
            {selectedInvoiceIds.length > 0 && (
              <div className="t-flex t-items-center t-gap-1 t-text-text-60 t-text-subtext">
                <span>Subtotal:</span>

                <AnimatedNumbers amount={subtotal} />
              </div>
            )}
            <span className="t-text-neutral-10 t-h-full">
              <DividerLine />
            </span>
            <TableSelectionControls
              groupId={group.uuid!}
              entityId={entityId}
              selectedInvoices={selectedInvoiceIds}
              onSelectionChange={onSelectionChange}
              rowSelection={uuidRowSelection}
              setRowSelection={setRowSelection}
            />
            <Button
              customType="ghost_icon"
              size="xsmall"
              onClick={() => onSelectionChange([])}
            >
              <Cross />
            </Button>
          </>
        </SelectedFloatingControl>
      )}
      {openBulkUpload.isOpen && (
        <BulkInvoiceUpload
          {...openBulkUpload}
          usePopulateMutation={usePopulateBulkInvoiceMutation}
          useUploadMutation={useUploadBulkInvoiceMutation}
          templateUrl={BULK_INVOICE_EXCEL_TEMPLATE_URL}
        />
      )}
      <Slider.Root open={isOpenSlider} onOpenChange={closeSlider}>
        <Slider.Content open={isOpenSlider} useCustomOverlay>
          <InvoiceSlider
            invoiceId={invoiceId}
            redirectLink={`/books/invoices-and-customers/${invoiceId}${search}`}
          />
        </Slider.Content>
      </Slider.Root>
    </>
  );
};
